export function formatPatientName(patientName) {
  if (!patientName || patientName.length === 0) {
    return '王*';
  }
  if (patientName.length === 2 || patientName.length === 3) {
    return patientName.charAt(0) + '*'.repeat(patientName.length - 1);
  } else if (patientName.length >= 4) {
    return patientName.substring(0, 2) + '*'.repeat(patientName.length - 2);
  } else {
    return patientName;
  }
}

export function formatTherapistName(name) {
  if (!name) return '';
  return String(name).replace(/\s*[（(]满级[）)]\s*/g, '').trim();
}

export function formatTimeDisplay(timeStr) {
  if (!timeStr) return '';
  const date = new Date(timeStr);
  if (isNaN(date.getTime())) return timeStr;
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function parseTimeString(timeStr) {
  if (!timeStr) return null;
  if (timeStr.includes('T') || timeStr.includes('-')) {
    const date = new Date(timeStr);
    return isNaN(date.getTime()) ? null : date;
  }
  const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (timeMatch) {
    const today = new Date();
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      today.setHours(hours, minutes, 0, 0);
      return today;
    }
  }
  return null;
}

export function parseTimeStringWithDate(timeStr, appointmentDate) {
  if (!timeStr || !appointmentDate) return null;
  if (timeStr.includes('T') || timeStr.includes('-')) {
    const date = new Date(timeStr);
    return isNaN(date.getTime()) ? null : date;
  }
  const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const d = new Date(appointmentDate);
    d.setHours(hours, minutes, 0, 0);
    return d;
  }
  return null;
}

export function getSleepStatus(item) {
  if (item.status === 'completed') {
    return '';
  }
  if (item.realtime_sleep_state) {
    return item.realtime_sleep_state;
  }
  return '清醒';
}

// 图片缓存对象，避免重复调用require()
const imageCache = {
  // 男性图片
  'male-深睡': null,
  'male-浅睡': null,
  'male-清醒': null,
  'male-default': null,
  // 女性图片
  'female-深睡': null,
  'female-浅睡': null,
  'female-清醒': null,
  'female-default': null,
  // 状态图标
  'status-已结束': null,
  'status-治疗中': null
};

// 初始化图片缓存（只执行一次）
function initImageCache() {
  if (imageCache['male-深睡'] !== null) {
    return; // 已经初始化过
  }
  
  try {
    // 预加载所有图片路径
    imageCache['male-深睡'] = require('@/assets/men/Male.png');
    imageCache['male-浅睡'] = require('@/assets/men/半眯男.png');
    imageCache['male-清醒'] = require('@/assets/men/睁眼男.png');
    imageCache['male-default'] = require('@/assets/men/Male.png');
    
    imageCache['female-深睡'] = require('@/assets/women/闭眼女.png');
    imageCache['female-浅睡'] = require('@/assets/women/半眯女.png');
    imageCache['female-清醒'] = require('@/assets/women/睁眼女.png');
    imageCache['female-default'] = require('@/assets/women/闭眼女.png');
    
    imageCache['status-已结束'] = require('@/assets/icon_治疗完成.png');
    imageCache['status-治疗中'] = require('@/assets/icon_治疗中.png');
  } catch (error) {
    logger.warn('图片缓存初始化失败:', error);
  }
}

// 初始化缓存
initImageCache();

export function getCharacterImage(item) {
  if (!item) {
    return imageCache['male-default'] || require('@/assets/men/Male.png');
  }
  const gender = (item.patientGender === 1) ? 'male' : 'female';
  const sleepStatus = getSleepStatus(item);
  const cacheKey = `${gender}-${sleepStatus}`;
  
  // 从缓存中获取，如果不存在则使用默认值
  const cachedImage = imageCache[cacheKey];
  if (cachedImage !== null) {
    return cachedImage;
  }
  
  // 如果缓存中没有，使用默认值并更新缓存
  const defaultKey = `${gender}-default`;
  return imageCache[defaultKey] || require('@/assets/men/Male.png');
}

export function getTreatmentStatus(appointment) {
  if (!appointment || !appointment.startTime || !appointment.endTime) {
    return '无数据';
  }
  if (appointment.isHistorical || appointment.treatmentStatus === '已结束') {
    return '已结束';
  }
  const now = new Date();
  let appointmentDate;
  if (appointment.appointmentDate) {
    appointmentDate = new Date(appointment.appointmentDate);
  } else {
    const startTimeDate = new Date(appointment.startTime);
    if (!isNaN(startTimeDate.getTime())) {
      appointmentDate = startTimeDate;
    } else {
      appointmentDate = new Date();
    }
  }
  const appointmentDateOnly = new Date(appointmentDate);
  appointmentDateOnly.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (appointmentDateOnly.getTime() < today.getTime()) {
    return '已结束';
  }
  if (appointmentDateOnly.getTime() > today.getTime()) {
    return '治疗未开始';
  }
  const startTime = parseTimeStringWithDate(appointment.startTime, appointmentDateOnly);
  const endTime = parseTimeStringWithDate(appointment.endTime, appointmentDateOnly);
  if (!startTime || !endTime) {
    return '时间格式错误';
  }
  if (now >= endTime) {
    return '已结束';
  }
  if (now < startTime) {
    return '治疗未开始';
  }
  return '治疗中';
}

export function getTreatmentStatusClass(item) {
  const status = getTreatmentStatus(item);
  if (status === '已结束' || status === '治疗即将完成') {
    return 'treatment-completed';
  } else {
    return 'treatment-ongoing';
  }
}

export function getTreatmentStatusIcon(item) {
  const status = getTreatmentStatus(item);
  const cacheKey = (status === '已结束' || status === '治疗即将完成') 
    ? 'status-已结束' 
    : 'status-治疗中';
  
  // 从缓存中获取
  const cachedIcon = imageCache[cacheKey];
  if (cachedIcon !== null) {
    return cachedIcon;
  }
  
  // 如果缓存中没有，fallback到require
  if (status === '已结束' || status === '治疗即将完成') {
    return require('@/assets/icon_治疗完成.png');
  } else {
    return require('@/assets/icon_治疗中.png');
  }
}

export function getBrainwaveDisplay(item, type) {
  if (!item) return 0;
  if (getTreatmentStatus(item) === '已结束') return 0;
  const v = item[type];
  return Math.round(v || 0);
}

export function generatePatientSeed(item) {
  const seedString = `${item.patientName || ''}_${item.roomName || ''}_${item.startTime || ''}`;
  let hash = 0;
  for (let i = 0; i < seedString.length; i++) {
    const char = seedString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

export function seededRandom(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

export function calculateEnergyScore(item) {
  if (!item || !item.startTime || !item.endTime) {
    return 0;
  }
  const now = new Date();
  const startTime = parseTimeString(item.startTime);
  const endTime = parseTimeString(item.endTime);
  if (!startTime) {
    return 0;
  }
  if (getTreatmentStatus(item) === '已结束') {
    const treatmentSeconds = Math.floor((endTime - startTime) / 1000);
    if (item.finalScore !== undefined) {
      return item.finalScore;
    }
    let totalScore = 0;
    const sleepStatus = getSleepStatus(item);
    const baseSeed = generatePatientSeed(item);
    for (let i = 0; i < treatmentSeconds; i++) {
      const seed = baseSeed + i;
      const random = seededRandom(seed) * 100;
      let scoreToAdd = 1;
      switch (sleepStatus) {
        case '深睡':
          if (random < 60) {
            scoreToAdd = 3;
          } else if (random < 90) {
            scoreToAdd = 2;
          } else {
            scoreToAdd = 1;
          }
          break;
        case '浅睡':
          if (random < 30) {
            scoreToAdd = 1;
          } else if (random < 90) {
            scoreToAdd = 2;
          } else {
            scoreToAdd = 3;
          }
          break;
        case '清醒':
          if (random < 90) {
            scoreToAdd = 1;
          } else {
            scoreToAdd = 2;
          }
          break;
      }
      totalScore += scoreToAdd;
    }
    item.finalScore = totalScore;
    return totalScore;
  }
  const treatmentSeconds = Math.floor((now - startTime) / 1000);
  if (!item.energyScore) {
    item.energyScore = 0;
    item.lastCalculatedSecond = 0;
  }
  const newSeconds = treatmentSeconds - (item.lastCalculatedSecond || 0);
  if (newSeconds > 0) {
    const sleepStatus = getSleepStatus(item);
    const baseSeed = generatePatientSeed(item);
    for (let i = 0; i < newSeconds; i++) {
      const currentSecond = (item.lastCalculatedSecond || 0) + i;
      const seed = baseSeed + currentSecond;
      const random = seededRandom(seed) * 100;
      let scoreToAdd = 1;
      switch (sleepStatus) {
        case '深睡':
          if (random < 60) {
            scoreToAdd = 3;
          } else if (random < 90) {
            scoreToAdd = 2;
          } else {
            scoreToAdd = 1;
          }
          break;
        case '浅睡':
          if (random < 30) {
            scoreToAdd = 1;
          } else if (random < 90) {
            scoreToAdd = 2;
          } else {
            scoreToAdd = 3;
          }
          break;
        case '清醒':
          if (random < 90) {
            scoreToAdd = 1;
          } else {
            scoreToAdd = 2;
          }
          break;
      }
      item.energyScore += scoreToAdd;
      if (i === newSeconds - 1) {
        item.lastAddedScore = scoreToAdd;
      }
    }
    item.lastCalculatedSecond = treatmentSeconds;
  }
  return item.energyScore;
}

export function getProgressPercentage(appointment) {
  if (!appointment || !appointment.startTime || !appointment.endTime) {
    return 0;
  }
  const startTime = parseTimeString(appointment.startTime);
  const endTime = parseTimeString(appointment.endTime);
  if (!startTime || !endTime) {
    return 0;
  }
  const totalSeconds = Math.floor((endTime - startTime) / 1000);
  const score = calculateEnergyScore(appointment);
  const percentage = Math.floor((score / totalSeconds) * 100);
  appointment.totalSeconds = totalSeconds;
  if (getTreatmentStatus(appointment) === '已结束') {
    if (appointment.finalPercentage === undefined) {
      const randomFinal = 100 + Math.floor(Math.random() * 51);
      appointment.finalPercentage = randomFinal;
      return randomFinal;
    }
    return appointment.finalPercentage;
  }
  return percentage;
}

export function getSleepBubbleValue(sleepStatus) {
  const random = Math.random() * 100;
  let bubbleValue = 1;
  switch (sleepStatus) {
    case '深睡':
      if (random < 60) {
        bubbleValue = 3;
      } else if (random < 90) {
        bubbleValue = 2;
      } else {
        bubbleValue = 1;
      }
      break;
    case '浅睡':
      if (random < 30) {
        bubbleValue = 1;
      } else if (random < 90) {
        bubbleValue = 2;
      } else {
        bubbleValue = 3;
      }
      break;
    case '清醒':
      if (random < 90) {
        bubbleValue = 1;
      } else {
        bubbleValue = 2;
      }
      break;
    default:
      bubbleValue = 1;
  }
  return bubbleValue;
}
